import { init, classModule, propsModule, styleModule, eventListenersModule, attributesModule } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Msg, Model, TagValue, Document } from "./types";
import { initialModel, update } from "./update";
import { view } from "./view/index";
import { syncEditorViews, destroyEditor, serialize, shownCardIds } from "./editor";
import { cardTitle } from "./card-title";
import { refreshAllViewBlocks } from "./editor/view-node-view";
import { hideLinkPopover } from "./editor/link-popover";
import { publishModel } from "./editor/cards";
import { renderLogin } from "./login";
import * as api from "./api";

const patch = init([classModule, propsModule, styleModule, eventListenersModule, attributesModule]);

let model = initialModel;
let vnode: VNode | Element = document.getElementById("app")!;

// Effect-interpreter state: debounce timer for topbar search.
let searchDebounce: ReturnType<typeof setTimeout> | null = null;
// Per-field debounce timers for CEL expression validation.
const checkDebounce: Record<string, ReturnType<typeof setTimeout> | null> = {};

// A node view can dispatch while the render builds it. Such a message waits in
// this queue until the current message is complete.
const queue: Msg[] = [];
let dispatching = false;

function dispatch(msg: Msg): void {
  queue.push(msg);
  if (dispatching) return;
  dispatching = true;
  try {
    for (let next = queue.shift(); next; next = queue.shift()) {
      // Each navigation hides the hover popover.
      if (next.type === "NavigateTo" || next.type === "GoHome") hideLinkPopover();
      const [newModel, effects] = update(next, model);
      model = newModel;
      render();
      runEffects(effects);
    }
  } finally {
    dispatching = false;
  }
}

// Log a failed effect and surface it to the user as a toast. `label` is the
// user-facing summary; the backend's reason (minus the HTTP status prefix the
// api layer adds) is appended when present.
function reportError(label: string, err: unknown): void {
  console.error(label, err);
  const raw = err instanceof Error ? err.message : String(err);
  const detail = raw.replace(/^\d+\s[^:]*:\s*/, "");
  dispatch({ type: "ShowToast", message: detail ? `${label} ${detail}` : label });
}

// ── Per-card save queue ───────────────────────────────────────────────────────
// One in-flight PUT per card, always issued against the server-confirmed hash.
// The queue (not the Model) owns the optimistic token, so the client can never
// collide with its own sequential saves — a 409 then means a *genuine* external
// edit (another tab/device), which we recover from by reloading.
type SaveChange = { tags?: Record<string, TagValue>; content?: Document };
type SaveState = {
  hash: string | undefined; // last server-confirmed state hash
  inFlight: boolean; // a PUT is outstanding
  buffer: SaveChange | null; // edits accumulated since the last commit
  timer: ReturnType<typeof setTimeout> | null; // debounce before committing
};
const saveQueues = new Map<string, SaveState>();
const SAVE_DEBOUNCE_MS = 500;
const POLL_MS = 4000;
// The smallest relative date is in minutes, so a tick each 30 s keeps it current.
const TICK_MS = 30_000;

function queueFor(id: string): SaveState {
  let q = saveQueues.get(id);
  if (!q) {
    q = { hash: undefined, inFlight: false, buffer: null, timer: null };
    saveQueues.set(id, q);
  }
  return q;
}

// Quiescent = no unsaved local state. Only then is the local hash known to equal
// the server's last write, so a differing server hash is safe to reload over.
function isQuiescent(q: SaveState): boolean {
  return !q.inFlight && q.buffer === null && q.timer === null;
}

// Record the authoritative hash from a load/create response. Never overwrites
// while a write is pending — that write will set a fresher hash itself.
function seedHash(id: string, hash: string): void {
  const q = queueFor(id);
  if (!isQuiescent(q)) return;
  q.hash = hash;
}

// Entry point for every edit to an existing card. Coalesces into the buffer and
// (re)arms the debounce. If a write is already in flight, the buffer is flushed
// when that write completes — against the fresh hash.
function requestSave(id: string, change: SaveChange): void {
  const q = queueFor(id);
  q.buffer = { ...q.buffer, ...change };
  if (q.inFlight) return;
  if (q.timer) clearTimeout(q.timer);
  q.timer = setTimeout(() => {
    q.timer = null;
    flush(id);
  }, SAVE_DEBOUNCE_MS);
}

function flush(id: string): void {
  const q = queueFor(id);
  if (q.inFlight || q.buffer === null) return;
  const change = q.buffer;
  q.buffer = null;
  q.inFlight = true;
  api
    .putCard({
      id,
      ...(q.hash !== undefined ? { lastStateHash: q.hash } : {}),
      ...(change.tags !== undefined ? { tags: change.tags } : {}),
      ...(change.content !== undefined ? { content: change.content } : {}),
    })
    .then((result) => {
      q.hash = result.stateHash;
      q.inFlight = false;
      dispatch({ type: "CardSaved", id: result.id, stateHash: result.stateHash });
      // Tag edits (incl. kanban drag) can change what views show — refetch.
      refreshAllViewBlocks();
      // Edits that arrived mid-flight: save them now, against the fresh hash.
      if (q.buffer !== null) flush(id);
    })
    .catch((err: unknown) => {
      q.inFlight = false;
      if (err instanceof api.HttpError && err.status === 409) {
        // Genuine external edit. Our buffered edits were made against a now-stale
        // base and can't be safely rebased, so drop them and reload the server's
        // version rather than clobber the other writer.
        q.buffer = null;
        reloadCard(id, "This card was edited elsewhere — reloaded.");
      } else {
        // Transient failure: keep the buffer so the next edit (or retry) flushes
        // it; don't lose the user's work.
        reportError("Couldn't save card.", err);
      }
    });
}

// Refetch a card and apply it, reseating the hash. Bails if the user has started
// editing since (queue no longer quiescent) so we never clobber unsaved input.
function reloadCard(id: string, toast?: string): void {
  api
    .fetchCard(id)
    .then((card) => {
      const q = queueFor(id);
      if (!isQuiescent(q)) return;
      q.hash = card.stateHash;
      dispatch({ type: "CardLoaded", card, reload: true });
      if (toast) dispatch({ type: "ShowToast", message: toast });
    })
    .catch((err: unknown) => reportError("Couldn't reload card.", err));
}

// Poll the hash of each card that the page shows in an editor: the current card
// and its open sections. Then a tab that was idle while another tab or device
// changed a card reloads the card before its first save gets a 409. The poll
// only reads, and it ignores a card with unsaved or in-flight state. Thus a
// reload cannot replace a save that is in progress: the server can have the
// hash of our PUT before our response arrives.
function pollShownCards(): void {
  for (const id of shownCardIds()) pollCardHash(id);
}

function pollCardHash(id: string): void {
  const q = saveQueues.get(id);
  if (!q || q.hash === undefined || !isQuiescent(q)) return;
  const expected = q.hash;
  api
    .fetchCardHash(id)
    .then((hash) => {
      // Check again after the await. The queue must be quiescent, and its hash
      // must be the same, so no save started or finished during the request.
      const q2 = saveQueues.get(id);
      if (!q2 || !isQuiescent(q2) || q2.hash !== expected) return;
      if (hash !== expected) reloadCard(id);
    })
    .catch(() => {
      /* best-effort: ignore transient poll failures */
    });
}

// The cards that a request reads now. Many node views can ask for one card,
// and they all get the result of one request.
const fetching = new Set<string>();

function fetchCard(id: string): void {
  if (fetching.has(id)) return;
  fetching.add(id);
  api
    .fetchCard(id)
    .then((card) => {
      seedHash(card.id, card.stateHash);
      dispatch({ type: "CardLoaded", card });
    })
    .catch((err: unknown) => {
      if (err instanceof api.HttpError && err.status === 404) {
        dispatch({ type: "CardMissing", id });
        // A link to a deleted card shows "Not Found", which is sufficient.
        if (id !== model.currentCardId) return;
      }
      reportError("Couldn't load card.", err);
    })
    .finally(() => fetching.delete(id));
}

function render(): void {
  // The editors and the node views that the patch makes read this Model.
  publishModel(model);
  const newVnode = view(model, dispatch);
  patch(vnode, newVnode);
  vnode = newVnode;
  syncEditorViews();
  updateDocumentTitle();
}

// Tab header: first line of the current card (same title inline links show),
// or "Cardlake" when not viewing a card.
function updateDocumentTitle(): void {
  const id = model.currentCardId;
  if (!id) {
    document.title = "Cardlake";
    return;
  }
  const state = model.editorStates[id];
  const doc = state ? serialize(state.doc) : model.cardCache[id]?.content;
  document.title = doc ? cardTitle(doc) : "Cardlake";
}

function runEffects(effects: ReturnType<typeof update>[1]): void {
  for (const effect of effects) {
    switch (effect.type) {
      case "FetchCard":
        fetchCard(effect.id);
        break;

      case "PutCard": {
        // Create a brand-new card (updates go through WriteCard / the queue).
        // Open the new card in a fresh browser tab, leaving this tab on the
        // current card. window.open must run synchronously inside the click's
        // call stack (before the async PUT resolves), or a popup blocker stops
        // it. The blank tab gets the address of the new card when its id arrives.
        const templateId = effect.templateId;
        const tab = window.open("about:blank", "_blank");
        const create = templateId
          ? api.fetchCard(templateId).then((template) => {
              // The new card is a normal card, not itself a template — drop the
              // template marker from the copied tags.
              const { template: _t, ...tags } = template.tags;
              return api.putCard({ tags, content: template.content });
            })
          : api.putCard({});
        create.then((result) => {
          seedHash(result.id, result.stateHash);
          if (tab) tab.location.href = cardUrl(result.id);
          // Popup blocked → fall back to navigating this tab.
          else dispatch({ type: "NavigateTo", id: result.id });
        }).catch((err: unknown) => {
          reportError("Couldn't create card.", err);
          if (tab) tab.close();
        });
        break;
      }

      case "CreateExtractedCard": {
        // As for PutCard, window.open must run in the call stack of the click.
        // A blocked popup is no problem: the new section shows the card.
        const { cardId, content } = effect;
        const tab = window.open("about:blank", "_blank");
        api.putCard({ content }).then((result) => {
          seedHash(result.id, result.stateHash);
          dispatch({ type: "CardExtracted", cardId, newId: result.id });
          if (tab) tab.location.href = cardUrl(result.id);
        }).catch((err: unknown) => {
          dispatch({ type: "ExtractFailed", cardId });
          reportError("Couldn't create card.", err);
          if (tab) tab.close();
        });
        break;
      }

      case "WriteCard": {
        const change: SaveChange = {};
        if (effect.tags !== undefined) change.tags = effect.tags;
        if (effect.content !== undefined) change.content = effect.content;
        requestSave(effect.id, change);
        break;
      }

      case "SearchCards": {
        const { query, target, tagKey, tagValue, orderKey, orderDir, orderByRelevance, limit } =
          effect;
        const run = () => {
          api.searchCards(query || undefined, {
            ...(tagKey !== undefined ? { tagKey } : {}),
            ...(tagValue !== undefined ? { tagValue } : {}),
            ...(orderKey !== undefined ? { orderKey } : {}),
            ...(orderDir !== undefined ? { orderDir } : {}),
            ...(orderByRelevance !== undefined ? { orderByRelevance } : {}),
            ...(limit !== undefined ? { limit } : {}),
          }).then((results) => {
            if (target === "modal") {
              dispatch({ type: "ModalSearchResults", results });
            } else {
              dispatch({ type: "SearchResults", results, query });
            }
          });
        };
        // Debounce the topbar search; modal/template search fires immediately.
        if (target === "search") {
          if (searchDebounce) clearTimeout(searchDebounce);
          searchDebounce = setTimeout(run, 250);
        } else {
          run();
        }
        break;
      }

      case "FetchGlobal":
        api.fetchGlobal().then((config) => {
          dispatch({ type: "GlobalLoaded", homeCardId: config.homeCardId ?? null });
        }).catch((err: unknown) => {
          reportError("Couldn't load settings.", err);
        });
        break;

      case "PutGlobal":
        api.putGlobal({ homeCardId: effect.homeCardId }).catch((err: unknown) => {
          reportError("Couldn't save settings.", err);
        });
        break;

      case "FetchRegistry":
        api.fetchRegistry().then((reg) => {
          dispatch({ type: "RegistryLoaded", tags: reg.tags, systemTags: reg.systemTags, hash: reg.stateHash });
        }).catch((err: unknown) => {
          reportError("Couldn't load tags.", err);
        });
        break;

      case "PutRegistry":
        api.putRegistry(effect.tags, effect.lastStateHash).then((outcome) => {
          if (outcome.status === "ok") {
            dispatch({ type: "RegistrySaved", tags: outcome.tags, hash: outcome.stateHash });
          } else if (outcome.status === "conflict") {
            dispatch({ type: "RegistryConflicts", conflicts: outcome.conflicts });
          } else {
            // Stale hash: someone else saved first — drop edits and reload.
            dispatch({ type: "RegistryReset" });
            api.fetchRegistry().then((reg) => {
              dispatch({ type: "RegistryLoaded", tags: reg.tags, systemTags: reg.systemTags, hash: reg.stateHash });
            }).catch(() => {});
          }
        }).catch((err: unknown) => {
          reportError("Couldn't save tags.", err);
        });
        break;

      case "FetchTokens":
        api.listTokens().then((tokens) => {
          dispatch({ type: "TokensLoaded", tokens });
        }).catch((err: unknown) => {
          reportError("Couldn't load API tokens.", err);
        });
        break;

      case "PostToken":
        api.createToken(effect.name, effect.scope).then(({ token, ...info }) => {
          dispatch({ type: "TokenCreated", token: info, secret: token });
        }).catch((err: unknown) => {
          reportError("Couldn't create the token.", err);
        });
        break;

      case "DeleteToken":
        api.deleteToken(effect.id).then(() => {
          dispatch({ type: "TokenDeleted", id: effect.id });
        }).catch((err: unknown) => {
          reportError("Couldn't revoke the token.", err);
        });
        break;

      case "CheckExpr": {
        // Debounce per field so the backend isn't hit on every keystroke.
        const { field, expr } = effect;
        if (checkDebounce[field]) clearTimeout(checkDebounce[field]!);
        // Emptying the (optional) field clears its error at once and cancels any
        // in-flight check, which would otherwise land a stale error.
        if (expr.trim() === "") {
          checkDebounce[field] = null;
          dispatch({ type: "ViewBlockExprChecked", field, error: null });
          break;
        }
        checkDebounce[field] = setTimeout(() => {
          api.celCheck(expr)
            .then((result) =>
              dispatch({
                type: "ViewBlockExprChecked",
                field,
                error: result.ok ? null : result.error ?? "Invalid expression",
              })
            )
            .catch(() => dispatch({ type: "ViewBlockExprChecked", field, error: "Validation failed" }));
        }, 250);
        break;
      }

      case "DeleteCard":
        api.deleteCard(effect.id).then(() => {
          dispatch({ type: "CardDeleted", id: effect.id });
          dispatch({ type: "GoHome" });
        }).catch((err: unknown) => {
          reportError("Couldn't delete card.", err);
        });
        break;

      case "SetUrlCard":
        window.history.pushState({ cardId: effect.id }, "", cardUrl(effect.id));
        break;

      case "ClearUrlCard": {
        const url = new URL(window.location.href);
        url.searchParams.delete("card");
        window.history.pushState({}, "", url.toString());
        break;
      }

      case "DestroyEditor":
        destroyEditor(effect.id);
        break;
    }
  }
}

// ── URL routing ───────────────────────────────────────────────────────────────

function cardUrl(id: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set("card", id);
  return url.toString();
}

function navigateFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const id = params.get("card");
  if (id) dispatch({ type: "NavigateTo", id });
}

window.addEventListener("popstate", (e) => {
  const state = e.state as { cardId?: string } | null;
  const cardId = state?.cardId ?? new URLSearchParams(window.location.search).get("card");
  if (cardId) {
    dispatch({ type: "NavigateTo", id: cardId });
  } else {
    dispatch({ type: "GoHome" });
  }
});

// Bootstrap. Without a session token, show the login screen instead of the
// app; a successful login reloads the page and we land here again with a token.
if (!api.getToken()) {
  renderLogin();
} else {
  render();
  navigateFromUrl();
  runEffects([{ type: "FetchGlobal" }, { type: "FetchRegistry" }]);
  setInterval(pollShownCards, POLL_MS);
  setInterval(() => dispatch({ type: "Tick", now: Date.now() }), TICK_MS);
}
